import React, { useContext, useCallback, useMemo, useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import OnboardingStepsUi from './ui/onboarding-steps.ui';
import { OnboardingStep } from './onboarding.type';
import { RootStoreContext } from '../core/root-store';
import { signOut } from '../user/user-update.service';
import { trackOnboardingStepComplete, trackOnboardingCompleted, trackOnboardingAbandoned, trackOnboardingStepEntry } from '../analytics/analytics.service';

import OnboardingStepPhoneNumberContainer from './screens/onboarding-step-phone-number.container';
import OnboardingStepUserPersonalInfoContainer from './screens/onboarding-step-user-personal-info.container';
import OnboardingStepRecentProjectContainer from './screens/onboarding-step-recent-project.container';
import OnboardingStepSkillsContainer from './screens/onboarding-step-skills.container';
import OnboardingStepRolePreferencesContainer from './screens/onboarding-step-role-preferences.container';
import OnboardingStepUploadDocumentsContainer from './screens/onboarding-step-upload-documents.container';

const ComponentByStep: Record<OnboardingStep, () => React.ReactElement> = {
  [OnboardingStep.PhoneNumber]: () => <OnboardingStepPhoneNumberContainer />,
  [OnboardingStep.PersonalInfo]: () => <OnboardingStepUserPersonalInfoContainer />,
  [OnboardingStep.RecentProject]: () => <OnboardingStepRecentProjectContainer />,
  [OnboardingStep.Skills]: () => <OnboardingStepSkillsContainer />,
  [OnboardingStep.RolePreferences]: () => <OnboardingStepRolePreferencesContainer />,
  [OnboardingStep.UploadDocuments]: () => <OnboardingStepUploadDocumentsContainer />,
  [OnboardingStep.OnboardingCompleted]: () => <div>Onboarding Completed</div>, // Placeholder - Should never render
};

const OnboardingStepContainer = observer(({ returned = false }: { returned?: boolean }) => {
  const rootStore = useContext(RootStoreContext);
  const store = rootStore.onboardingStore;
  const { role } = store.formData;
  const returnedUser = returned;
  const jobPosition = store.formData.webOnboarding?.appliedFor;
  const isAppliedForPosition = jobPosition ? jobPosition.length > 0 : false;

  const {
    step,
    stepOrder,
    stepIndex,
    progress,
    nextStep,
    previousStep,
  } = store;

  const pages = useMemo(() => (
    stepOrder.map((i) => ComponentByStep[i])
  ), [stepOrder]);

  // Track step entry when user first sees a step
  useEffect(() => {
    trackOnboardingStepEntry(step);
  }, [step, role]);

  const goToNextStep = useCallback(async() => {
    store.markStepAttempted();

    if (!store.continueable) {
      return;
    }

    if (store.documents.profileImage.selectedFile) {
      const uploadSuccess = await store.uploadFile('profileImage');
      if (!uploadSuccess) {
        return;
      }
    }

    if (store.documents.projectImage.selectedFile) {
      const uploadSuccess = await store.uploadFile('projectImage');
      if (!uploadSuccess) {
        return;
      }
    }

    // Save current step data before advancing
    const saveSuccess = await store.saveCurrentStepData();
    if (!saveSuccess) {
      return;
    }

    trackOnboardingStepComplete(step);

    if (nextStep) {
      store.updateOnboardingStep(nextStep);
    } else {
      // Last step completed - redirect to p-test
      trackOnboardingCompleted();
      // Navigation to p-test will happen here
    }
  }, [nextStep, step, store]);

  const goToPreviousStep = useCallback(() => {
    if (previousStep) {
      store.updateOnboardingStep(previousStep);
    }
  }, [previousStep, store]);

  const handleSignOut = useCallback(async () => {
    try {
      trackOnboardingAbandoned(step);
      // Store user role for redirect after sign-out
      const userRole = rootStore.userStore.user?.role;
      if (userRole) {
        sessionStorage.setItem('lastUserRole', userRole);
      }
      await signOut();
    } catch (error) {}
  }, [step, rootStore.userStore.user?.role]);

  if (!store.formData || store.formData.webOnboarding?.onboardingStep === undefined) {
    return null;
  }

  return (
    <OnboardingStepsUi
      returnedUser={returnedUser}
      pages={pages}
      progress={progress}
      index={stepIndex}
      userRole={role || ''}
      isAppliedForPosition={isAppliedForPosition}
      onContinue={goToNextStep}
      onBack={goToPreviousStep}
      onSignOut={handleSignOut}
    />
  );
});

export default OnboardingStepContainer;
